import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { DropdownMenu, IconButton } from 'seagull';
import { Kebab } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useAgreementRowMenuItems } from '~/components/AgreementActions/AgreementRowMenuItems';
import { useAgreementRowActions } from '~/components/AgreementActions/useAgreementRowActions';
import { useDraftRowMenuItems } from '~/components/DraftActions/DraftRowMenuItems';
import { useDraftRowActions } from '~/components/DraftActions/useDraftRowActions';
import { useProposalRowMenuItems } from '~/components/ProposalActions/ProposalRowMenuItems';
import { useOverlayContext, useStore } from '~/contexts';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useProposalActions } from '~/hooks/useProposalActions';
import { assertUnreachable } from '~/infra/utils';
import { RelationshipStatus } from '~/models/relationship';
import ActivityLogModal from '~/pages/ProposalsDashboard/ActivityLogModal/ActivityLogModal';
import { selectors } from '~/store/state-selectors';
import { toRelationshipProgressStatus } from './RecentAgreementMenu.utils';
import { openClientPreview } from './useRecentAgreementNavigation.utils';
export const GetRecentAgreementMenuTID = (parentTestId)=>getTestIds(parentTestId);
// WHY: the overview endpoint's classifier drops IsSample items, so widget rows are never samples.
const NON_SAMPLE = false;
function AgreementRowMenu(param) {
    let { row, customerId, customerName, isArchived, refetch, optimisticallyRemove, testIds } = param;
    var _row_relationshipId, _row_draftId;
    const isVendor = useStore(selectors.user.isVendor);
    const actions = useAgreementRowActions({
        onTerminateSuccess: ()=>optimisticallyRemove(row.id),
        onUpdateSuccess: ()=>refetch()
    });
    const menuItems = useAgreementRowMenuItems({
        agreement: {
            id: (_row_relationshipId = row.relationshipId) !== null && _row_relationshipId !== void 0 ? _row_relationshipId : row.id,
            draftId: (_row_draftId = row.draftId) !== null && _row_draftId !== void 0 ? _row_draftId : undefined,
            isSample: NON_SAMPLE,
            customerName,
            customerBusinessId: customerId,
            status: RelationshipStatus.Approved
        },
        isVendor,
        isTerminated: false,
        readOnly: isArchived,
        testId: testIds.Kebab(),
        heapId: 'client-detail-agreement',
        onEditAgreement: actions.onEditAgreement,
        onRenameAgreement: actions.onRenameAgreement,
        onRenameSuccess: ()=>refetch(),
        onChangeAssignee: actions.onChangeAssignee,
        onDuplicateAgreement: actions.onDuplicateAgreement,
        onDownloadPDF: actions.onDownloadPDF,
        onTerminateAgreement: actions.onTerminateAgreement
    });
    return /*#__PURE__*/ _jsx(DropdownMenu, {
        gap: 0,
        testId: testIds.ActiveMenu(),
        target: /*#__PURE__*/ _jsx(IconButton, {
            bi: {
                name: 'client_detail_agreement_menu'
            },
            testId: testIds.Kebab(),
            skin: "transparent",
            children: /*#__PURE__*/ _jsx(Kebab, {
                size: 20
            })
        }),
        children: menuItems
    });
}
function ProposalRowMenu(param) {
    let { row, customerId, refetch, testIds } = param;
    var _useStore, _row_relationshipId, _row_draftId;
    const isVendor = useStore(selectors.user.isVendor);
    const vendorId = (_useStore = useStore(selectors.user.businessId)) !== null && _useStore !== void 0 ? _useStore : '';
    const proposalActions = useProposalActions();
    const overlayContext = useOverlayContext();
    const { downloadAgreementPdf } = useDownloadAgreementPdf();
    const menuRow = {
        id: (_row_relationshipId = row.relationshipId) !== null && _row_relationshipId !== void 0 ? _row_relationshipId : row.id,
        draftId: (_row_draftId = row.draftId) !== null && _row_draftId !== void 0 ? _row_draftId : undefined,
        isSample: NON_SAMPLE,
        vendorId,
        customerId,
        progressStatus: toRelationshipProgressStatus(row.progressStatus)
    };
    const menuItems = useProposalRowMenuItems({
        proposal: menuRow,
        isVendor,
        expired: false,
        proposalActions,
        onViewProposal: (proposal)=>openClientPreview(proposal.id, overlayContext),
        onViewActivityLog: (proposal)=>overlayContext.showModal({
                biName: 'activity_log_modal',
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(ActivityLogModal, {
                        relationshipId: proposal.id,
                        dismissModal: dismissModal
                    });
                }
            }),
        onDownloadPDF: (proposal)=>downloadAgreementPdf(proposal.id),
        refresh: async ()=>refetch()
    });
    return /*#__PURE__*/ _jsx(DropdownMenu, {
        gap: 0,
        testId: testIds.ProposalMenu(),
        target: /*#__PURE__*/ _jsx(IconButton, {
            bi: {
                name: 'client_detail_agreement_menu'
            },
            testId: testIds.Kebab(),
            skin: "transparent",
            children: /*#__PURE__*/ _jsx(Kebab, {
                size: 20
            })
        }),
        children: menuItems
    });
}
function DraftRowMenu(param) {
    let { row, refetch, testIds } = param;
    var _row_draftId;
    const isScheduled = row.status === 'scheduled';
    const actions = useDraftRowActions({
        draftId: (_row_draftId = row.draftId) !== null && _row_draftId !== void 0 ? _row_draftId : '',
        isSample: NON_SAMPLE,
        isScheduled,
        refresh: ()=>refetch()
    });
    const menuItems = useDraftRowMenuItems({
        index: 0,
        isScheduled,
        onEdit: actions.onEdit,
        onPreview: actions.onPreview,
        onSendNow: actions.onSendNow,
        onCancelScheduling: actions.onCancelScheduling,
        onDuplicate: actions.onDuplicate,
        onArchive: actions.onArchive,
        onDelete: actions.onDelete
    });
    return /*#__PURE__*/ _jsx(DropdownMenu, {
        gap: 0,
        testId: testIds.DraftMenu(),
        target: /*#__PURE__*/ _jsx(IconButton, {
            bi: {
                name: 'client_detail_agreement_menu'
            },
            testId: testIds.Kebab(),
            skin: "transparent",
            children: /*#__PURE__*/ _jsx(Kebab, {
                size: 20
            })
        }),
        children: menuItems
    });
}
export function RecentAgreementMenu(param) {
    let { row, customerId, customerName, isArchived, refetch, optimisticallyRemove, testId } = param;
    const { RecentAgreementMenuTestIds } = useMemo(()=>GetRecentAgreementMenuTID(testId), [
        testId
    ]);
    const subMenuProps = {
        row,
        customerId,
        customerName,
        isArchived,
        refetch,
        optimisticallyRemove,
        testIds: RecentAgreementMenuTestIds
    };
    switch(row.status){
        case 'active':
            return /*#__PURE__*/ _jsx(AgreementRowMenu, _object_spread({}, subMenuProps));
        case 'paymentConnected':
        case 'reviewed':
        case 'sent':
            return /*#__PURE__*/ _jsx(ProposalRowMenu, _object_spread({}, subMenuProps));
        case 'scheduled':
        case 'draft':
            return /*#__PURE__*/ _jsx(DraftRowMenu, _object_spread({}, subMenuProps));
        default:
            return assertUnreachable(row.status);
    }
}
